import React, { FC, ReactElement, ReactNode, useEffect } from 'react';
import {
  Card,
  FooterCard,
  HighlightedCard,
} from '@edreamsodigeo/prisma-design-system';
import {
  useItineraryActions,
  useGoToDesktopPassengersPage,
  getSelectedItinerary,
} from '../../../../clientState/providers/ShoppingBundleProvider/ActionsProvider';
import {
  useHistory,
  useTracking,
  useTrackElement,
  createFeature,
} from '@edreamsodigeo/frontend-core';
import { usePrime } from 'clientState/providers/PrimeProvider';
import { useFeeType } from 'clientState/providers/FeeTypeProvider';
import {
  MEMBER_PRICE_POLICY_DISCOUNTED,
  MEMBER_PRICE_POLICY_UNDISCOUNTED,
} from '../../../Shared/FeeSelector/FeeTypeName/feeTypeConstantNames';
import {
  TRIP_SUMMARY_PAGE,
  SEM_TRIP_SUMMARY_PAGE,
} from 'clientState/providers/NavigationProvider';
import { useDisplayedItinerariesQuery } from 'clientState/providers/DisplayedItinerariesProvider';
import {
  RESULTS_CATEGORY,
  SEM_RESULTS_CATEGORY,
} from 'clientState/providers/TrackingProviders';
import {
  DEAL_HOTTEST,
  DEAL_WEEKEND,
} from 'clientState/hooks/useGetBuyPathDeal';
import FlexibleDatesRepricingMessageDecorator, {
  FlexibleDatesRepricingMessagePositionType,
} from '@components/Results/Components/FlexibleDates/FlexibleDatesRepricingMessage/FlexibleDatesRepricingMessageDecorator';
import AirlineCampaignDecorationRevamp from './Campaigns/AirlineCampaign/AirlineCampaignDecoration';
import ItineraryDecorationRevamp from './ItineraryDecoration';
import PrimeDayItineraryHeaderRevamp from './Campaigns/PrimeDay/PrimeDayItineraryHeader';
import PriceTendency from './PriceTendency';
import UKNoPrimeFareMessage from './UKNoPrimeFareMessage';
import { useFlowTools } from 'flowTools/useFlowTools';
import { useFavoriteLayerActions } from 'clientState/providers/FavoriteLayerProvider';
import { useSemItinerarySelectionActions } from 'clientState/providers/SEM/SemItinerarySelectionProvider/hooks';
import { useAlternativeItineraryLayerActions } from 'clientState/providers/AlternativeItineraryProvider/AlternativeItineraryLayerProvider';
import {
  useAlternativeItinerary,
  useAlternativeItineraryActions,
} from 'clientState/providers/AlternativeItineraryProvider/AlternativeItineraryCalculatorProvider';
import { useFlightOfTheDayItineraryIds } from 'clientState/providers/SearchResponseProvider';
import AlternativeItineraryDecorator from '@components/AlternativeItineraryDoubleCard/components/AlternativeItineraryDecorator/AlternativeItineraryDecorator';
import { useItinerarySelection } from 'clientState/providers/ItinerarySelectionProvider';
import HighlightedDecorator from '@components/Results/Components/ThreeColumnsItinerary/HighlightedDecorator/HighlightedDecorator';
import ItineraryUnavailableOverlay from '@components/Results/Components/ThreeColumnsItinerary/ItineraryUnavailableOverlay/ItineraryUnavailableOverlay';
import PrimeDiscountCrown, {
  shouldDisplayPrimeCrown,
} from './PrimeDiscountCrown/PrimeDiscountCrown';
import { getPrices } from '@components/Results/Components/Itinerary/ResultsItineraryPrice/ResultsItineraryDualPrice';
import { shouldDisplayPrimePill } from './PrimeDiscountPill/PrimeDiscountPill';
import { useEqualProminencePrimeDisplayMobile } from 'clientState/hooks/useEqualProminencePrimeDisplayMobile';
import { HighlightedType } from '@components/Results/Components/ItineraryHeader/constants/highlightedTypes';
import { useItinerary } from '@components/Results/Components/Itinerary/Provider';
import {
  PRIME_DAYS,
  FLIGHT_OF_THE_DAY_DECORATION,
  STANDARD_CARD_UK_MOBILE,
  ITINERARY_DEAL_MOBILE,
} from 'clientState/ab/aliases';
import { useFlightOfTheDayTracking } from '@components/Results/Components/Itinerary/useFlightOfTheDayTracking';
import { ThreeColumnsItineraryType, ItineraryDecorationProps } from './types';
import { useResultsDataMeshTracking } from 'clientState/hooks/useItineraryListTracking/useResultsDataMeshTracking';
import ThreeColumnsItineraryLayout from './ThreeColumnsItineraryLayout';

type ThreeColumnsItineraryProps = {
  itinerary: ThreeColumnsItineraryType;
  position: number;
  hasRetailingTechniques?: boolean;
  isOnFavoritesLayer?: boolean;
  isSem?: boolean;
  isAlternativeItinerary?: boolean;
  isSelectedItinerary?: boolean;
  hasAlternativeItineraryDecorator?: boolean;
  isAlternativeTopSection?: boolean;
  nonIssuable?: boolean;
  isClickable?: boolean;
  PriceDescription?: ReactNode;
  showAlternativeFlightLayer?: boolean;
  isUnavailable?: boolean;
  goToTripSummaryPage?: (options?: { feeTypeId?: string }) => void;
  isAlternativeItineraryOnModal?: boolean;
  isEqualProminencePrimeDisplayMobile?: boolean;
  onSelectAlternativeItinerary?: () => void;
  onSelectOriginalItinerary?: () => void;
  overrideAction?: boolean;
  trackingHandler?: (
    event: string,
    clickedElement: string,
    closingMethod?: string
  ) => void;
};

const ItineraryPrimeDaysDecoration: FC<ItineraryDecorationProps> = ({
  children,
  highlightedType,
  primeDayConfig,
  hasRetailingTechniques,
  isAlternativeItinerary,
  hasAlternativeItineraryDecorator,
  isAlternativeItineraryOnModal,
  isUnavailable = false,
  ...props
}) => {
  const { isPrimeDayFare, primeDayFareType } = primeDayConfig || {};
  const primeDayCondition =
    isPrimeDayFare && primeDayFareType && primeDayFareType !== 'BRONZE';
  const Component =
    (highlightedType === 'recommended' || primeDayCondition) &&
    hasRetailingTechniques &&
    !isAlternativeItinerary &&
    !isUnavailable
      ? HighlightedCard
      : Card;

  let type;
  if (highlightedType === 'recommended' && hasRetailingTechniques) {
    type = 'positive';
  } else if (primeDayCondition && hasRetailingTechniques) {
    type = 'promotional';
  }

  const sx =
    (isAlternativeItinerary && !hasAlternativeItineraryDecorator) ||
    isUnavailable
      ? {
          backgroundColor: 'neutral.20',
          border: '1px solid',
          borderColor: 'neutral.60',
        }
      : {};

  const sxOnLayer =
    !isAlternativeItinerary && isAlternativeItineraryOnModal
      ? { border: '1px solid', borderColor: 'neutral.60', boxShadow: 'none' }
      : {};

  const withPrimeDayHeader =
    primeDayCondition &&
    primeDayFareType === 'GOLD' &&
    highlightedType !== HighlightedType.Recommended &&
    !isAlternativeItinerary &&
    !isUnavailable;

  return (
    <ThreeColumnsItineraryDecoration
      Component={Component}
      highlightedType={highlightedType}
      hasRetailingTechniques={hasRetailingTechniques}
      type={type}
      withPrimeDayHeader={withPrimeDayHeader}
      {...props}
      sx={{ ...sx, ...sxOnLayer }}
    >
      {children}
    </ThreeColumnsItineraryDecoration>
  );
};

const ThreeColumnsItineraryDecorationBase = ({
  Component,
  highlightedType,
  withPrimeDayHeader,
  type,
  sx,
  children,
  ...props
}) => {
  return (
    <FlexibleDatesRepricingMessageDecorator
      position={FlexibleDatesRepricingMessagePositionType.belowCard}
    >
      {withPrimeDayHeader && <PrimeDayItineraryHeaderRevamp />}
      <HighlightedDecorator
        CardComponent={Component}
        type={type}
        highlightedType={highlightedType}
        {...props}
        sx={sx}
      >
        {children}
      </HighlightedDecorator>
    </FlexibleDatesRepricingMessageDecorator>
  );
};

const ThreeColumnsItineraryDecorationRevamp = ({
  Component,
  highlightedType,
  hasRetailingTechniques,
  withPrimeDayHeader,
  type,
  sx,
  children,
  itinerary,
  position,
  ...props
}) => {
  const { trackFlightOfTheDay } = useFlightOfTheDayTracking('show');
  const flightOfTheDayItineraryIds = useFlightOfTheDayItineraryIds();
  const { id } = useItinerary();
  const isFlightOfTheDay =
    (flightOfTheDayItineraryIds.includes(id) &&
      highlightedType !== HighlightedType.Recommended &&
      highlightedType !== HighlightedType.Personalised) ||
    highlightedType === HighlightedType.FlightOfTheDay;

  useEffect(() => {
    if (isFlightOfTheDay) {
      trackFlightOfTheDay(itinerary, position, hasRetailingTechniques);
    }
  }, [
    isFlightOfTheDay,
    hasRetailingTechniques,
    itinerary,
    position,
    trackFlightOfTheDay,
  ]);

  let CardComponent = Component;

  if (isFlightOfTheDay && hasRetailingTechniques) {
    highlightedType = HighlightedType.FlightOfTheDay;
    CardComponent = Card;
  }

  return (
    <FlexibleDatesRepricingMessageDecorator
      position={FlexibleDatesRepricingMessagePositionType.belowCard}
    >
      {withPrimeDayHeader &&
        highlightedType !== HighlightedType.FlightOfTheDay && (
          <PrimeDayItineraryHeaderRevamp />
        )}
      <HighlightedDecorator
        CardComponent={CardComponent}
        type={type}
        highlightedType={highlightedType}
        {...props}
        sx={sx}
      >
        {children}
      </HighlightedDecorator>
    </FlexibleDatesRepricingMessageDecorator>
  );
};

const ThreeColumnsItineraryDecoration = createFeature({
  name: FLIGHT_OF_THE_DAY_DECORATION,
  variations: [
    ThreeColumnsItineraryDecorationBase,
    ThreeColumnsItineraryDecorationRevamp,
    ThreeColumnsItineraryDecorationRevamp,
  ],
  version: 1,
});

const ItineraryFooterFullPrimePrice = ({
  onClick,
  isAlternativeItinerary,
}): ReactElement => {
  return (
    <FooterCard onClick={onClick}>
      <UKNoPrimeFareMessage isAlternativeItinerary={isAlternativeItinerary} />
    </FooterCard>
  );
};

const ItineraryDecorationMobile = createFeature({
  name: PRIME_DAYS,
  variations: [
    AirlineCampaignDecorationRevamp,
    ItineraryPrimeDaysDecoration,
    ItineraryPrimeDaysDecoration,
  ],
  version: 1,
});

const ItineraryDealDecoration = createFeature({
  name: ITINERARY_DEAL_MOBILE,
  variations: [
    ItineraryDecorationMobile,
    ItineraryDecorationRevamp,
    ItineraryDecorationRevamp,
  ],
  version: 1,
});

const ItineraryFooterComponent = createFeature({
  name: STANDARD_CARD_UK_MOBILE,
  variations: [ItineraryFooterFullPrimePrice, null],
  version: 1,
});

const ThreeColumnsItineraryDefault: FC<ThreeColumnsItineraryProps> = ({
  itinerary,
  position,
  hasRetailingTechniques = true,
  isOnFavoritesLayer = false,
  isSem = false,
  isAlternativeItinerary = false,
  isSelectedItinerary = false,
  hasAlternativeItineraryDecorator = false,
  isAlternativeTopSection = false,
  isClickable = true,
  isUnavailable = false,
  PriceDescription,
  goToTripSummaryPage = (): void => {},
  isAlternativeItineraryOnModal = false,
  isEqualProminencePrimeDisplayMobile = false,
  onSelectAlternativeItinerary,
  overrideAction,
  trackingHandler,
  onSelectOriginalItinerary,
}) => {
  const { isDesktop } = useFlowTools();
  const { navigate } = useHistory() as any;
  const { showItineraryTrip } = useItineraryActions();
  const { data } = useDisplayedItinerariesQuery();
  const { highlightedType, campaignConfig } = itinerary;
  const primeDayConfig = campaignConfig?.primeDayConfig || undefined;
  const { isUK, isPrimeTotalPriceOnCard, isPrimeSite, isPrimeUser } =
    usePrime() || {};
  const { trackEvent } = useTracking();
  const [feeType] = useFeeType();
  const goToNextPage = useGoToDesktopPassengersPage();
  const { close } = useFavoriteLayerActions();
  const isMeta = data && data.externalSelection;
  const { showSemItineraryTrip } = useSemItinerarySelectionActions();
  const { legs } = itinerary;
  const isRoundTrip = legs.length > 1;
  const { close: closeAlternative } = useAlternativeItineraryLayerActions();
  const setItineraryInfo = useItinerarySelection();
  const { primePrice, noPrimePrice } = getPrices(itinerary.fees);
  const { selectedItinerary, discountAmount, altSearchId } =
    useAlternativeItinerary();
  const { createDMTrackingLabelOnClickCard } = useResultsDataMeshTracking();

  const trackingAlternativeContinueElement = useTrackElement(
    ['click'],
    `alternative-flights-continue-original-flight-searchId-${altSearchId}-itineraryId-${selectedItinerary?.id}-alternativeItineraryId-${itinerary?.id}`
  );
  const trackingAlternativeChangeElement = useTrackElement(
    ['click'],
    `alternative-flights-change-flight-searchId-${altSearchId}-itineraryId-${selectedItinerary?.id}-alternativeItineraryId-${itinerary?.id}-discount:${discountAmount?.amount}${discountAmount?.currency}`
  );

  const trackingOnClickCard = useTrackElement(
    ['click'],
    createDMTrackingLabelOnClickCard({
      itinerary,
      isPrime: feeType?.id === MEMBER_PRICE_POLICY_DISCOUNTED,
    })
  );

  const trackingRef = !isAlternativeItinerary
    ? trackingOnClickCard
    : isAlternativeTopSection
    ? trackingAlternativeContinueElement
    : trackingAlternativeChangeElement;

  const sendTracking = (forcedFeeTypeId): void => {
    const feeTypeId = forcedFeeTypeId || feeType?.id;
    const isPrimePriceSelected = feeTypeId === MEMBER_PRICE_POLICY_DISCOUNTED;
    const trackLabel =
      isUK || isPrimeTotalPriceOnCard || isEqualProminencePrimeDisplayMobile
        ? `continue_with_${
            isPrimePriceSelected ? 'prime' : 'standard'
          }_sce:no-log_pag:res`
        : `pre-select-info_submitted_${
            isPrimePriceSelected ? '' : 'non'
          }member`;
    trackEvent({
      action:
        isUK || isPrimeTotalPriceOnCard || isEqualProminencePrimeDisplayMobile
          ? 'prime'
          : 'unlocked',
      label: trackLabel,
    });
  };

  const onclickSemItineraryTracking = (): void => {
    const label = `s_pos:${position}-pri:${itinerary.price?.amount}-go:${
      itinerary.legs[0].segments[0].carrier.name
    }-re:${
      itinerary.legs[itinerary.legs.length - 1].segments[0].carrier.name
    }-ty:${isRoundTrip ? 'RT' : 'OW'}`;
    trackEvent({
      category: SEM_RESULTS_CATEGORY,
      action: 'searcher_flights',
      label,
    });
    if (!isClickable) return;

    navigate(SEM_TRIP_SUMMARY_PAGE, { push: true });
    showSemItineraryTrip(itinerary, position, {});
  };
  const onClickAlternativeCard = (): void => {
    if (trackingHandler) {
      trackingHandler(
        'select',
        isSelectedItinerary ? 'original' : 'alternative',
        'card'
      );
    }
    if (!isClickable) return;

    if (overrideAction) {
      if (onSelectAlternativeItinerary) {
        onSelectAlternativeItinerary();
      } else if (onSelectOriginalItinerary) {
        onSelectOriginalItinerary();
      }
    } else {
      setItineraryInfo({
        itinerary: getSelectedItinerary(itinerary, feeType?.id),
      });

      if (isDesktop) {
        goToNextPage();
      } else {
        navigate(TRIP_SUMMARY_PAGE, { push: true });
        showItineraryTrip();
      }
    }

    closeAlternative();
  };

  // Itinerary functions
  const openItineraryTrip = (props?): void => {
    if (isPrimeSite) sendTracking(props?.feeTypeId);
    if (!isClickable) return;

    const options = isPrimeSite && !props ? { feeTypeId: feeType?.id } : props;
    goToTripSummaryPage(options);
  };

  const getItineraryFooter = (
    isAlternativeItinerary: boolean
  ): React.ReactElement | null => {
    if (
      isAlternativeItinerary ||
      isAlternativeItineraryOnModal ||
      isEqualProminencePrimeDisplayMobile
    ) {
      return null;
    }

    return (
      <>
        {isUK && !isPrimeUser && (
          <ItineraryFooterComponent
            isAlternativeItinerary={isAlternativeItinerary}
            onClick={(): void =>
              openItineraryTrip({
                feeTypeId: MEMBER_PRICE_POLICY_UNDISCOUNTED,
              })
            }
          />
        )}
        {isPrimeTotalPriceOnCard && !isPrimeUser && (
          <ItineraryFooterFullPrimePrice
            isAlternativeItinerary={isAlternativeItinerary}
            onClick={(): void =>
              openItineraryTrip({
                feeTypeId: MEMBER_PRICE_POLICY_UNDISCOUNTED,
              })
            }
          />
        )}
      </>
    );
  };

  const handleOnClickItinerary = (
    e: React.MouseEvent<HTMLDivElement>
  ): void => {
    e.stopPropagation();
    if (
      itinerary.id &&
      noPrimePrice &&
      primePrice &&
      (shouldDisplayPrimePill(noPrimePrice, primePrice) ||
        shouldDisplayPrimeCrown(noPrimePrice, primePrice))
    ) {
      trackEvent({
        category: RESULTS_CATEGORY,
        action: 'prime_pill',
        label: `prime_pill_click:${
          shouldDisplayPrimeCrown(noPrimePrice, primePrice) ? 'C' : 'P'
        }_pag:res`,
      });
    }
    if (isOnFavoritesLayer) {
      close();
      return isDesktop ? goToNextPage() : openItineraryTrip();
    }

    return isAlternativeItinerary || isSelectedItinerary
      ? onClickAlternativeCard()
      : isSem
      ? onclickSemItineraryTracking()
      : openItineraryTrip();
  };

  const shouldShowPrimeDiscountCrown =
    highlightedType !== 'recommended' &&
    hasRetailingTechniques &&
    !isAlternativeItinerary;

  return shouldShowPrimeDiscountCrown ? (
    <PrimeDiscountCrown
      primePrice={primePrice}
      noPrimePrice={noPrimePrice}
      onClick={handleOnClickItinerary}
    >
      <div ref={trackingRef}>
        <ItineraryDecorationMobile
          onClick={handleOnClickItinerary}
          data-testid={isSem ? 'itinerary-sem-card' : 'itinerary'}
          highlightedType={highlightedType}
          primeDayConfig={primeDayConfig}
          campaignConfig={campaignConfig}
          isMeta={isMeta}
          itinerary={itinerary}
          position={position}
          footer={getItineraryFooter(isAlternativeItinerary)}
          hasRetailingTechniques={hasRetailingTechniques}
          isAlternativeItinerary={isAlternativeItinerary}
          hasAlternativeItineraryDecorator={hasAlternativeItineraryDecorator}
          isUnavailable={isUnavailable}
          isAlternativeItineraryOnModal={isAlternativeItineraryOnModal}
          sx={{ position: 'relative' }}
        >
          <ThreeColumnsItineraryLayout
            itinerary={itinerary}
            position={position}
            isSem={isSem}
            hasRetailingTechniques={hasRetailingTechniques}
            PriceDescription={PriceDescription}
            isAlternativeItinerary={isAlternativeItinerary}
            isAlternativeTopSection={isAlternativeTopSection}
            isAlternativeItineraryOnModal={isAlternativeItineraryOnModal}
            isSelectedItinerary={isSelectedItinerary}
            isUnavailableItinerary={isUnavailable}
          />
        </ItineraryDecorationMobile>
        {hasRetailingTechniques && !isAlternativeItineraryOnModal && (
          <PriceTendency />
        )}
      </div>
    </PrimeDiscountCrown>
  ) : hasAlternativeItineraryDecorator ? (
    <div ref={trackingRef}>
      <AlternativeItineraryDecorator
        onClick={handleOnClickItinerary}
        isAlternativeItineraryOnModal={isAlternativeItineraryOnModal}
      >
        <ThreeColumnsItineraryLayout
          itinerary={itinerary}
          position={position}
          isSem={isSem}
          hasRetailingTechniques={hasRetailingTechniques}
          PriceDescription={PriceDescription}
          isAlternativeItinerary={isAlternativeItinerary}
          hasAlternativeItineraryDecorator={hasAlternativeItineraryDecorator}
          isAlternativeItineraryOnModal={isAlternativeItineraryOnModal}
          isSelectedItinerary={isSelectedItinerary}
          isUnavailableItinerary={isUnavailable}
        />
      </AlternativeItineraryDecorator>
    </div>
  ) : highlightedType === DEAL_WEEKEND || highlightedType === DEAL_HOTTEST ? (
    <div ref={trackingRef}>
      <ItineraryDealDecoration
        onClick={handleOnClickItinerary}
        data-testid={isSem ? 'itinerary-sem-card' : 'itinerary'}
        highlightedType={highlightedType}
        primeDayConfig={primeDayConfig}
        campaignConfig={campaignConfig}
        isMeta={isMeta}
        itinerary={itinerary}
        position={position}
        footer={getItineraryFooter(isAlternativeItinerary)}
        hasRetailingTechniques={hasRetailingTechniques}
        isAlternativeItinerary={isAlternativeItinerary}
        isUnavailable={isUnavailable}
        isAlternativeItineraryOnModal={isAlternativeItineraryOnModal}
        sx={{ position: 'relative' }}
      >
        <ThreeColumnsItineraryLayout
          itinerary={itinerary}
          position={position}
          isSem={isSem}
          hasRetailingTechniques={hasRetailingTechniques}
          PriceDescription={PriceDescription}
          isAlternativeItinerary={isAlternativeItinerary}
          isAlternativeItineraryOnModal={isAlternativeItineraryOnModal}
          isSelectedItinerary={isSelectedItinerary}
          isUnavailableItinerary={isUnavailable}
        />
      </ItineraryDealDecoration>
    </div>
  ) : (
    <div ref={trackingRef}>
      <ItineraryDecorationMobile
        onClick={handleOnClickItinerary}
        data-testid={isSem ? 'itinerary-sem-card' : 'itinerary'}
        highlightedType={highlightedType}
        primeDayConfig={primeDayConfig}
        campaignConfig={campaignConfig}
        isMeta={isMeta}
        itinerary={itinerary}
        position={position}
        footer={getItineraryFooter(isAlternativeItinerary)}
        hasRetailingTechniques={hasRetailingTechniques}
        isAlternativeItinerary={isAlternativeItinerary}
        hasAlternativeItineraryDecorator={hasAlternativeItineraryDecorator}
        isUnavailable={isUnavailable}
        isAlternativeItineraryOnModal={isAlternativeItineraryOnModal}
        sx={{ position: 'relative' }}
      >
        <ThreeColumnsItineraryLayout
          itinerary={itinerary}
          position={position}
          isSem={isSem}
          hasRetailingTechniques={hasRetailingTechniques}
          PriceDescription={PriceDescription}
          isAlternativeItinerary={isAlternativeItinerary}
          isAlternativeTopSection={isAlternativeTopSection}
          isAlternativeItineraryOnModal={isAlternativeItineraryOnModal}
          isSelectedItinerary={isSelectedItinerary}
          isUnavailableItinerary={isUnavailable}
        />
      </ItineraryDecorationMobile>
      {hasRetailingTechniques && !isAlternativeItineraryOnModal && (
        <PriceTendency />
      )}
    </div>
  );
};

const ThreeColumnsItineraryEPWrapper = (props: ThreeColumnsItineraryProps) => {
  const isEqualProminencePrimeDisplayMobile =
    useEqualProminencePrimeDisplayMobile();
  return (
    <ThreeColumnsItineraryDefault
      {...props}
      isEqualProminencePrimeDisplayMobile={isEqualProminencePrimeDisplayMobile}
    />
  );
};

const ThreeColumnsItineraryEP = createFeature({
  name: 'PRIME_PRIMO70M',
  variations: [ThreeColumnsItineraryDefault, ThreeColumnsItineraryEPWrapper],
  version: 1,
});

const ThreeColumnsItinerary = (props) => {
  const { itinerary, showAlternativeFlightLayer } = props;
  const { isDesktop } = useFlowTools();
  const { open: openAlternativeItineraryLayer } =
    useAlternativeItineraryLayerActions();
  const { selectedItinerary, continueWithErrorBookeable } =
    useAlternativeItinerary();
  const {
    requestAlternativeItinerary,
    hasAlternativeItineraryCarrier,
    isItineraryUnavailable,
    setContinueWithErrorBookeableState,
  } = useAlternativeItineraryActions();
  const { navigate } = useHistory() as any;
  const { showItineraryTrip = () => {} } = useItineraryActions();

  const isUnavailable = isItineraryUnavailable(itinerary);

  const goToTripSummaryPage = (options): void => {
    if (
      !isDesktop &&
      hasAlternativeItineraryCarrier(itinerary) &&
      showAlternativeFlightLayer
    ) {
      openAlternativeItineraryLayer();
      requestAlternativeItinerary(itinerary);
    } else {
      navigate(TRIP_SUMMARY_PAGE, { push: true });
      showItineraryTrip(options);
    }
  };

  if (continueWithErrorBookeable && selectedItinerary?.id === itinerary.id) {
    navigate(TRIP_SUMMARY_PAGE, { push: true });
    showItineraryTrip();
    setContinueWithErrorBookeableState(false);
  }

  return (
    <ItineraryUnavailableOverlay withOverlay={isUnavailable}>
      <ThreeColumnsItineraryEP
        {...props}
        isUnavailable={isUnavailable}
        goToTripSummaryPage={goToTripSummaryPage}
      />
    </ItineraryUnavailableOverlay>
  );
};

export default ThreeColumnsItinerary;
